/**
 * Collector Guide email gate: subscribe via Klaviyo Client API (no Shopify /contact).
 * Instagram in-app browsers fail Cloudflare on POST /contact; this posts to a.klaviyo.com.
 *
 * Bot harden: honeypot + min dwell + dotted-local reject + Tor-exit / FranTech
 * hosting-CIDR reject (fail-closed when lists loaded and IP unknown). Make: form_type
 * collector-guide (+ location / UTMs). Keep guards in sync with
 * scripts/lib/collector-guide-gate-guards.js.
 */
(function () {
  var KLAVIYO_REVISION = '2024-10-15';
  var METRIC_NAME = 'Collector Guide Downloaded';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var MIN_DWELL_MS = 3000;
  var TOR_CHECK_MS = 2000;
  var IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
  var torExitMap = null;
  var torListPromise = null;
  var hostingCidrs = null;
  var hostingCidrPromise = null;
  var clientIpv4Promise = null;

  function onCollectorGuidePage() {
    if (document.body && document.body.classList.contains('template-collector-guide')) return true;
    return /\/pages\/collector-guide\/?$/.test(window.location.pathname || '');
  }

  function restorePageScroll() {
    var html = document.documentElement;
    var body = document.body;
    function isLocked(el) {
      if (!el) return false;
      var overflow = String(el.style.overflow || el.style.overflowY || '').toLowerCase();
      return overflow === 'hidden' || el.classList.contains('overflow-hidden') || el.classList.contains('klaviyo-prevent-body-scrolling');
    }
    function clearLock(el) {
      if (!el) return;
      el.style.removeProperty('overflow');
      el.style.removeProperty('overflow-y');
      el.style.removeProperty('overflow-x');
      el.style.removeProperty('position');
      el.style.removeProperty('height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('touch-action');
      el.style.removeProperty('padding-right');
      el.classList.remove('overflow-hidden');
      el.classList.remove('klaviyo-prevent-body-scrolling');
    }
    if (isLocked(html) || isLocked(body)) {
      clearLock(html);
      clearLock(body);
    }
  }

  function hideKlaviyoChrome() {
    var nodes = document.querySelectorAll(
      '[class*="kl-private"], [class*="kl-teaser"], [class*="klaviyo-form"], [id*="klaviyo-form"], .klaviyo-form-preview-wrapper'
    );
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].style.setProperty('display', 'none', 'important');
      nodes[i].style.setProperty('visibility', 'hidden', 'important');
      nodes[i].style.setProperty('pointer-events', 'none', 'important');
      nodes[i].setAttribute('aria-hidden', 'true');
    }
    restorePageScroll();
  }

  function suppressKlaviyoOnsitePopup() {
    if (!onCollectorGuidePage()) return;
    function closeForm(formId) {
      if (!formId) return;
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['closeForm', formId]);
    }
    window.addEventListener('klaviyoForms', function (e) {
      var type = e.detail && e.detail.type;
      var formId = e.detail && e.detail.formId;
      hideKlaviyoChrome();
      if (!formId) return;
      if (type === 'open' || type === 'embedOpen' || type === 'teaserOpen') {
        closeForm(formId);
        setTimeout(function () {
          closeForm(formId);
          hideKlaviyoChrome();
        }, 50);
        setTimeout(function () {
          closeForm(formId);
          hideKlaviyoChrome();
        }, 400);
      }
    });
    hideKlaviyoChrome();
    [0, 400, 1200, 3000, 6000, 10000].forEach(function (ms) {
      setTimeout(hideKlaviyoChrome, ms);
    });
    if (typeof MutationObserver === 'function') {
      var scheduled = false;
      function scheduleHide() {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          hideKlaviyoChrome();
        }, 0);
      }
      var observer = new MutationObserver(function () {
        scheduleHide();
      });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
      if (document.body) {
        observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'], childList: true });
      }
    }
  }
  suppressKlaviyoOnsitePopup();

  function companyIdFromPage() {
    var scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].getAttribute('src') || '';
      var match = src.match(/company_id=([A-Za-z0-9]+)/);
      if (match) return match[1];
    }
    return '';
  }

  function klaviyoHeaders() {
    return {
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      revision: KLAVIYO_REVISION
    };
  }

  /** Keep in sync with scripts/lib/collector-guide-gate-guards.js */
  function isSuspiciousEmail(email) {
    var raw = String(email || '').trim().toLowerCase();
    var at = raw.indexOf('@');
    if (at < 1) return true;
    var local = raw.slice(0, at);
    if (local.indexOf('..') !== -1) return true;
    var dots = (local.match(/\./g) || []).length;
    if (dots >= 4) return true;
    if (dots >= 3 && local.length >= 20) return true;
    return false;
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('timeout'));
      }, ms);
      promise.then(
        function (value) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        },
        function (err) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  function loadTorExits(url) {
    if (torExitMap) return Promise.resolve(torExitMap);
    if (torListPromise) return torListPromise;
    if (!url) return Promise.resolve(null);
    torListPromise = fetch(url, { credentials: 'omit', cache: 'force-cache' })
      .then(function (res) {
        if (!res.ok) throw new Error('tor list ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var list = Array.isArray(data) ? data : [];
        var map = Object.create(null);
        for (var i = 0; i < list.length; i++) {
          var ip = String(list[i] || '').trim();
          if (IPV4_RE.test(ip)) map[ip] = true;
        }
        torExitMap = map;
        return map;
      })
      .catch(function () {
        torListPromise = null;
        return null;
      });
    return torListPromise;
  }

  function ipv4ToInt(ip) {
    var n = String(ip || '').trim();
    if (!IPV4_RE.test(n)) return null;
    var p = n.split('.');
    var out = 0;
    for (var i = 0; i < 4; i++) {
      var o = Number(p[i]);
      if (!Number.isInteger(o) || o < 0 || o > 255) return null;
      out = (out << 8) + o;
    }
    return out >>> 0;
  }

  function parseCidr(cidr) {
    var s = String(cidr || '').trim();
    var m = s.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})$/);
    if (!m) return null;
    var ip = ipv4ToInt(m[1]);
    var bits = Number(m[2]);
    if (ip == null || bits < 0 || bits > 32) return null;
    var mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
    return { net: (ip & mask) >>> 0, mask: mask };
  }

  function isHostingDenyIp(ip, cidrs) {
    var n = ipv4ToInt(ip);
    if (n == null || !cidrs || !cidrs.length) return false;
    for (var i = 0; i < cidrs.length; i++) {
      var c = cidrs[i];
      if (!c) continue;
      if ((n & c.mask) >>> 0 === c.net) return true;
    }
    return false;
  }

  function loadHostingCidrs(url) {
    if (hostingCidrs) return Promise.resolve(hostingCidrs);
    if (hostingCidrPromise) return hostingCidrPromise;
    if (!url) return Promise.resolve(null);
    hostingCidrPromise = fetch(url, { credentials: 'omit', cache: 'force-cache' })
      .then(function (res) {
        if (!res.ok) throw new Error('hosting cidrs ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var list = Array.isArray(data) ? data : [];
        var out = [];
        for (var i = 0; i < list.length; i++) {
          var c = parseCidr(list[i]);
          if (c) out.push(c);
        }
        hostingCidrs = out;
        return out;
      })
      .catch(function () {
        hostingCidrPromise = null;
        return null;
      });
    return hostingCidrPromise;
  }

  function lookupClientIpv4() {
    if (clientIpv4Promise) return clientIpv4Promise;
    clientIpv4Promise = fetch('https://ipv4.icanhazip.com/', { credentials: 'omit', cache: 'no-store' })
      .then(function (res) {
        if (!res.ok) throw new Error('ip ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var ip = String(text || '').trim();
        if (!IPV4_RE.test(ip)) throw new Error('bad ip');
        return ip;
      })
      .catch(function () {
        clientIpv4Promise = null;
        return null;
      });
    return clientIpv4Promise;
  }

  /** Keep in sync with scripts/lib/collector-guide-gate-guards.js shouldBlockSubmitIp. */
  function shouldBlockSubmitIp(map, cidrs, ip) {
    var listsReady = map != null || cidrs != null;
    var n = String(ip || '').trim();
    if (IPV4_RE.test(n)) {
      if (map && map[n] === true) return true;
      if (cidrs && isHostingDenyIp(n, cidrs)) return true;
      return false;
    }
    return !!listsReady;
  }

  /**
   * Fail-closed when deny lists loaded and IPv4 unknown (timeout / echo blocked).
   * Fail-open only if lists never loaded.
   */
  function isBlockedSubmitIp(root) {
    var torUrl = root && root.getAttribute('data-tor-exits-url');
    var cidrUrl = root && root.getAttribute('data-hosting-cidrs-url');
    return withTimeout(
      Promise.all([loadTorExits(torUrl), loadHostingCidrs(cidrUrl), lookupClientIpv4()]).then(function (triple) {
        return shouldBlockSubmitIp(triple[0], triple[1], triple[2]);
      }),
      TOR_CHECK_MS
    ).catch(function () {
      return shouldBlockSubmitIp(torExitMap, hostingCidrs, null);
    });
  }

  function readUtms() {
    var out = { utm_source: '', campaign: '' };
    try {
      var params = new URLSearchParams(window.location.search || '');
      out.utm_source = params.get('utm_source') || '';
      out.campaign = params.get('utm_campaign') || params.get('campaign') || '';
    } catch (e) {}
    return out;
  }

  function subscribe(companyId, listId, email, source) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Collector Guide',
          profile: {
            data: {
              type: 'profile',
              // Client Subscriptions API: do NOT nest profile.subscriptions
              // (Klaviyo 400: "'subscriptions' is not a valid field for the resource 'profile'").
              // Posting to /client/subscriptions/ is the consent act.
              attributes: {
                email: email,
                properties: {
                  collector_guide: true,
                  signup_source: 'collector-guide'
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: klaviyoHeaders(),
      body: JSON.stringify(body)
    });
  }

  function uniqueId(email) {
    return 'cg-' + Date.now() + '-' + String(email || '').toLowerCase().replace(/[^a-z0-9@._-]/g, '').slice(0, 48);
  }

  function trackDownload(companyId, email, location) {
    var body = {
      data: {
        type: 'event',
        attributes: {
          unique_id: uniqueId(email),
          properties: {
            source: 'collector-guide-gate',
            location: location || ''
          },
          metric: {
            data: {
              type: 'metric',
              attributes: { name: METRIC_NAME }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: { collector_guide: true }
              }
            }
          }
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: klaviyoHeaders(),
      body: JSON.stringify(body)
    });
  }

  /** Identify only — metric fires once via Client Events API (avoids double count). */
  function identifyOnsite(email) {
    try {
      window.klaviyo = window.klaviyo || [];
      var identify = { $email: email, email: email, collector_guide: true };
      if (typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify(identify);
      } else {
        window.klaviyo.push(['identify', identify]);
      }
    } catch (e) {}
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function fireDownloadMetric(companyId, email, location) {
    identifyOnsite(email);
    return trackDownload(companyId, email, location)
      .then(function (res) {
        if (res && (res.ok || res.status === 202)) return res;
        throw new Error('track ' + (res && res.status));
      })
      .catch(function () {
        return delay(800).then(function () {
          identifyOnsite(email);
          return trackDownload(companyId, email, location);
        });
      });
  }

  function showError(root, message) {
    var el = root.querySelector('[data-cg-gate-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearError(root) {
    var el = root.querySelector('[data-cg-gate-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function unlock(root) {
    var form = root.querySelector('[data-klaviyo-client-subscribe]');
    var success = root.querySelector('[data-cg-gate-success]');
    if (form) form.hidden = true;
    if (success) {
      success.hidden = false;
      if (typeof success.focus === 'function') {
        try {
          success.focus();
        } catch (e) {}
      }
    }
    if (typeof window.gwfineTrackSignup === 'function') {
      window.gwfineTrackSignup('Collector Guide');
    } else if (typeof window.studioTrackSignup === 'function') {
      window.studioTrackSignup('Collector Guide');
    }
  }

  function fakeSuccess(root) {
    try {
      sessionStorage.setItem('cg-gate-unlocked', '1');
    } catch (e) {}
    var gates = document.querySelectorAll('[data-collector-guide-gate]');
    for (var g = 0; g < gates.length; g++) {
      unlock(gates[g]);
    }
  }

  function sendMake(email, location) {
    if (typeof window.sendToMake !== 'function') return;
    var now = new Date().toISOString();
    var utms = readUtms();
    window
      .sendToMake({
        form_type: 'collector-guide',
        create_date: now,
        last_update: now,
        email: email,
        first_name: '',
        source: 'collector-guide',
        tags: 'collector-guide,newsletter,stay-connected',
        location: location || '',
        utm_source: utms.utm_source,
        campaign: utms.campaign
      })
      .catch(function () {});
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.hasAttribute('data-klaviyo-client-subscribe')) return;

    var root = form.closest('[data-collector-guide-gate]');
    if (!root) return;

    var listId = (root.getAttribute('data-klaviyo-list-id') || '').trim();
    var companyId = (root.getAttribute('data-klaviyo-company-id') || '').trim() || companyIdFromPage();
    var location = root.getAttribute('data-location') || '';

    if (!listId || !companyId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var input = form.querySelector('input[type="email"]');
    var email = input ? String(input.value || '').trim() : '';
    var button = form.querySelector('[type="submit"]');
    var honeypot = form.querySelector('[data-cg-honeypot]');
    var honeypotValue = honeypot ? honeypot.value : '';

    clearError(root);

    // Honeypot filled → fake success, no Klaviyo / Make
    if (String(honeypotValue || '').trim() !== '') {
      fakeSuccess(root);
      return;
    }

    var readyAt = Number(root.getAttribute('data-cg-ready-at') || 0);
    if (!readyAt || Date.now() - readyAt < MIN_DWELL_MS) {
      showError(root, 'One moment — please try again.');
      return;
    }

    if (!EMAIL_RE.test(email) || isSuspiciousEmail(email)) {
      showError(root, 'Please enter a valid email address.');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
      }
      return;
    }
    if (input) input.removeAttribute('aria-invalid');

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    isBlockedSubmitIp(root).then(function (blocked) {
      if (blocked) {
        fakeSuccess(root);
        return;
      }

      subscribe(companyId, listId, email, 'Collector Guide')
        .then(function (res) {
          if (!res.ok && res.status !== 202) {
            throw new Error('subscribe ' + res.status);
          }
          return fireDownloadMetric(companyId, email, location).catch(function () {
            return null;
          });
        })
        .then(function () {
          try {
            sessionStorage.setItem('cg-gate-unlocked', '1');
          } catch (e) {}
          sendMake(email, location);
          var gates = document.querySelectorAll('[data-collector-guide-gate]');
          for (var g = 0; g < gates.length; g++) {
            unlock(gates[g]);
          }
        })
        .catch(function () {
          showError(root, 'Something went wrong. Please try again.');
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        });
    });
  }

  function init() {
    if (document.__cgGateBound) return;
    document.__cgGateBound = true;
    document.addEventListener('submit', onSubmit, true);

    var roots = document.querySelectorAll('[data-collector-guide-gate]');
    var readyAt = String(Date.now());
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      root.setAttribute('data-cg-ready-at', readyAt);
      loadTorExits(root.getAttribute('data-tor-exits-url') || '');
      loadHostingCidrs(root.getAttribute('data-hosting-cidrs-url') || '');
      lookupClientIpv4();
      var unlocked = false;
      try {
        unlocked = sessionStorage.getItem('cg-gate-unlocked') === '1';
      } catch (e) {}
      if (unlocked) unlock(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
